import React from "react";
import FilterBar from "./FilterBar";
import Pagination from "../Pagination";
import {
    StyledDataTableRoot,
    StyledHeaderIcon,
    StyledMessageCell,
    StyledSortIcon,
    StyledTable,
    StyledTableWrapper,
    StyledTd,
    StyledTh,
} from "./DataTable.styles";

/**
 * Generic AIE paginated/searchable/sortable table: a FilterBar toolbar plus a column-driven body
 * and optional Pagination footer. Content/business logic comes from `columns[].render`/`filterSlot`.
 * @param {Array<{key, label, icon, ariaLabel, align, width, sortable, sortField, render}>}
 *   [props.columns=[]] `icon` (Font Awesome name) renders a header icon — pair with `ariaLabel`.
 * @param {Object} [props.pagination] Forwarded to `Pagination`; omit to render no footer.
 * Also: data, keyField, loading, sortKey, sortOrder, onSortChange (called with the
 *   already-computed next direction), filterSlot, emptyMessage, loadingMessage, searchValue,
 *   onSearchChange, onReset, exportOptions, disabled, disabledReason (self-explanatory).
 */
function DataTable({
    columns = [],
    data = [],
    keyField = "id",
    loading = false,
    loadingMessage = "Loading...",
    emptyMessage = "No results found.",
    sortKey,
    sortOrder = "ASC",
    onSortChange,
    searchValue,
    onSearchChange,
    onReset,
    filterSlot,
    exportOptions,
    disabled = false,
    disabledReason,
    pagination,
}) {
    const getRowKey = (row, index) => (
        typeof keyField === "function" ? keyField(row, index) : row[keyField] ?? index
    );

    const handleHeaderClick = (column) => {
        if (column.sortable === false || !onSortChange) {
            return;
        }
        const field = column.sortField || column.key;
        const nextOrder = sortKey === field && sortOrder === "ASC" ? "DESC" : "ASC";
        onSortChange(field, nextOrder);
    };

    return (
        <StyledDataTableRoot>
            <FilterBar
                searchValue={searchValue}
                onSearchChange={onSearchChange}
                onReset={onReset}
                exportOptions={exportOptions}
                disabled={disabled}
                disabledReason={disabledReason}
            >
                {filterSlot}
            </FilterBar>

            <StyledTableWrapper>
                <StyledTable>
                    <thead>
                        <tr>
                            {columns.map((column) => {
                                const field = column.sortField || column.key;
                                const isSortable = column.sortable !== false;
                                return (
                                    <StyledTh
                                        key={column.key}
                                        $width={column.width}
                                        $align={column.align}
                                        $sortable={isSortable}
                                        aria-label={column.ariaLabel}
                                        onClick={() => handleHeaderClick(column)}
                                    >
                                        {column.icon && <StyledHeaderIcon className={`fal fa-${column.icon}`} aria-hidden="true" />}
                                        {column.label}
                                        {isSortable && (
                                            <StyledSortIcon
                                                $active={sortKey === field}
                                                className={`fal ${sortKey === field ? (sortOrder === "ASC" ? "fa-angle-up" : "fa-angle-down") : "fa-angles-up-down"}`}
                                                aria-hidden="true"
                                            />
                                        )}
                                    </StyledTh>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <StyledMessageCell colSpan={columns.length}>{loadingMessage}</StyledMessageCell>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <StyledMessageCell colSpan={columns.length}>{emptyMessage}</StyledMessageCell>
                            </tr>
                        ) : (
                            data.map((row, index) => (
                                <tr key={getRowKey(row, index)}>
                                    {columns.map((column) => (
                                        <StyledTd key={column.key} $align={column.align}>
                                            {column.render ? column.render(row) : row[column.key]}
                                        </StyledTd>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </StyledTable>
            </StyledTableWrapper>

            {pagination && <Pagination {...pagination} disabled={disabled} disabledReason={disabledReason} />}
        </StyledDataTableRoot>
    );
}

DataTable.displayName = "DataTable";

export default DataTable;
